import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid,
  Card, CardContent, Button, Chip,
  Rating, TextField, IconButton,
  Pagination,
  useMediaQuery,
  useTheme,
  Collapse,
  MenuItem,
  Select,
  InputLabel,
  FormControl, Modal, Snackbar, Alert,
} from '@mui/material';
import {
  Assessment, ExpandMore, ExpandLess,
  CheckCircleOutline, StarOutline,
  AddCircleOutline,
  Cancel,
} from '@mui/icons-material';
import GoalDetails from '../../../components/GoalDetails';
import axios from 'axios';
import { localAxiosGet, localAxios, axiosInstance } from '../../../Axios/axios';

const predefinedAppraisalCategories = [
  'Communication Skills',
  'Teamwork & Collaboration',
  'Problem-Solving',
  'Technical Proficiency',
  'Leadership',
  'Time Management',
  'Adaptability',
  'Customer Focus',
  'Innovation',
  'Quality of Work',
];

const AddPerformanceReviewAndRatings = ({ selectedemployeeId, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [employeeGoals, setEmployeeGoals] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isGoalDetailsPopupOpen, setIsGoalDetailsPopupOpen] = useState(false);
  const [expandedFeedbackId, setExpandedFeedbackId] = useState(null);
  const [appraisalPeriod, setAppraisalPeriod] = useState('');
  const [categories, setCategories] = useState([]);
  const [nextCategoryId, setNextCategoryId] = useState(1);
  const [showAppraisalForm, setShowAppraisalForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [feedback, setFeedback] = useState({
    strengths: '',
    areasForImprovement: '',
    goalsForNextPeriod: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });


  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const itemsPerPage = 4;

  useEffect(() => {
    fetchEmployeeGoals();
  }, [currentPage, selectedemployeeId]);

  const fetchEmployeeGoals = async () => {
    try {
      setIsLoading(true);
      const response = await axios(localAxiosGet(`/empGoal/get_All_goals_by_emp_id?emp_id=${selectedemployeeId}&page=${currentPage}&limit=${itemsPerPage}`));

      if (response.data.statusCode === 200) {
        const currentYear = new Date().getFullYear(); // 2025

        // Fixed filtering logic - check both start_date and createdAt
        const currentYearGoals = response.data.result.goals.filter(goal => {
          // Check start_date first
          if (goal.start_date) {
            const goalYear = new Date(goal.start_date).getFullYear();
            return goalYear === currentYear;
          }
          // If no start_date, check createdAt as fallback
          if (goal.createdAt) {
            const createdYear = new Date(goal.createdAt).getFullYear();
            return createdYear === currentYear;
          }
          // If neither date exists, exclude the goal
          return false;
        });

        console.log('All goals from API:', response.data.result.goals.length);
        console.log('Filtered current year goals:', currentYearGoals.length);
        console.log('Current year goals:', currentYearGoals);

        setEmployeeGoals(currentYearGoals);
        setTotalPages(response.data.result.totalPages);
      }
    } catch (error) {
      console.error('Error fetching employee goals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 4. Update your handleSubmitAppraisal function
 const handleSubmitAppraisal = async () => {
  // Validation
  const isValid = categories.every(cat => cat.name.trim() !== '' && cat.rating > 0);
  if (!isValid) {
    setSnackbar({
      open: true,
      message: 'Please ensure all selected categories have a name and a rating greater than 0.',
      severity: 'warning'
    });
    return;
  }

  // Check if feedback fields are filled
  if (!feedback.strengths.trim() || !feedback.areasForImprovement.trim() || !feedback.goalsForNextPeriod.trim()) {
    setSnackbar({
      open: true,
      message: 'Please fill in all feedback fields (Strengths, Areas for Improvement, Goals for Next Period).',
      severity: 'warning'
    });
    return;
  }

  // Check if employee has goals
  // if (employeeGoals.length === 0) {
  //   setSnackbar({
  //     open: true,
  //     message: 'Employee has no goals for the current year. Cannot submit appraisal.',
  //     severity: 'warning'
  //   });
  //   return;
  // }

  try {
    setIsSubmitting(true);

    // Get the first goal ID (or you might want to let the user select which goal)
    // For simplicity, using the first goal's ID
    const goalId = employeeGoals[0]?.id;

    // if (!goalId) {
    //   setSnackbar({
    //     open: true,
    //     message: 'No valid goal ID found for the employee.',
    //     severity: 'error'
    //   });
    //   return;
    // }

    // Prepare payload according to expected structure
    const payload = {
      report_manager_id: 249, // Get from props or context
      emp_id: parseInt(selectedemployeeId),
      goalId: goalId, // Add this required field
      feedback: `Strengths: ${feedback.strengths}\nAreas for Improvement: ${feedback.areasForImprovement}\nGoals for Next Period: ${feedback.goalsForNextPeriod}`,
      strengths: feedback.strengths,
      goals_for_next_period: feedback.goalsForNextPeriod,
      area_of_improvement: feedback.areasForImprovement,     
      appraisal_year: new Date().getFullYear(),
      appraisal_period: appraisalPeriod,
      // appraisal_period_id: appraisalPeriod,
       appraisal_period_id: 1,
      ratings: categories.map(cat => ({
        categoryName: cat.name,
        rating: cat.rating
      }))
    };

    console.log('Submitting Appraisal Payload:', JSON.stringify(payload, null, 2));

    // Make API call
    const response = await axiosInstance(localAxios('/performancemanagement/addRatings', payload));

    if (response.data.statusCode === 200) {
      // Show success snackbar
      setSnackbar({
        open: true,
        message: 'Appraisal saved successfully!',
        severity: 'success'
      });

      // Reset form
      setAppraisalPeriod('');
      setCategories([]);
      setNextCategoryId(1);
      setShowAppraisalForm(false);
      setFeedback({
        strengths: '',
        areasForImprovement: '',
        goalsForNextPeriod: ''
      });

      // Close snackbar after 3 seconds
      setTimeout(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
      }, 3000);
    }

  } catch (error) {
    console.error('Error submitting appraisal:', error);

    let errorMessage = 'Failed to save appraisal. Please try again.';

    if (error.response?.data?.statusCode === 400) {
      errorMessage = error.response.data.message;
    }

    // Show error snackbar
    setSnackbar({
      open: true,
      message: errorMessage,
      severity: 'error'
    });

    // Close snackbar after 5 seconds
    setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, 5000);
  } finally {
    setIsSubmitting(false);
  }
};

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleToggleFeedback = (id) => {
    setExpandedFeedbackId(prevId => (prevId === id ? null : id));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'in progress':
        return 'primary';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const renderGoalTypeChip = (type, isPredefined) => {
    let chipColor;
    let icon;

    if (type === 'Business' || type === 'Business Goal') {
      chipColor = 'primary';
      icon = <CheckCircleOutline sx={{ fontSize: 16 }} />;
    } else if (type === 'Personal' || type === 'Personal Goal') {
      chipColor = 'secondary';
      icon = <StarOutline sx={{ fontSize: 16 }} />;
    } else {
      chipColor = 'default';
      icon = null;
    }

    return (
      <Chip
        label={isPredefined ? `${type} (Predefined)` : type}
        color={chipColor}
        size="small"
        icon={icon}
        sx={{ mr: 1, mb: 0.5 }}
      />
    );
  };

  const handlePeriodChange = (event) => {
    setAppraisalPeriod(event.target.value);
    setShowAppraisalForm(true);
    setCategories([]);
    setNextCategoryId(1);
    if (event.target.value && categories.length === 0) {
      handleAddCategory();
    }
  };

  const handleAddCategory = () => {
    setCategories([...categories, { id: nextCategoryId, name: '', rating: 0 }]);
    setNextCategoryId(nextCategoryId + 1);
  };

  const handleRemoveCategory = (id) => {
    setCategories(categories.filter(cat => cat.id !== id));
    if (categories.length === 1 && categories[0].id === id) {
      setShowAppraisalForm(false);
      setNextCategoryId(1);
      setAppraisalPeriod('');
    }
  };

  const handleCategoryChange = (id, field, value) => {
    setCategories(categories.map(cat =>
      cat.id === id ? { ...cat, [field]: value } : cat
    ));
  };



  const handleGoalDetailsPopupOpen = () => {
    setIsGoalDetailsPopupOpen(true);
  };

  const Loader = () => <Box>Loading...</Box>;

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f0f4f8', borderRadius: '12px', minHeight: '80vh' }}>
      {isLoading && <Loader />}
      <Box sx={{ flexGrow: 1, overflow: 'auto', borderRadius: '12px', p: 2 }}>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: '12px',
          bgcolor: 'white',
          mb: 3
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            p: 2,
            borderRadius: '4px',
            color: 'white',
            justifyContent: 'space-between',
            width: '100%'
          }}>
            <Typography variant="h5" component="span" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment />
              Performance Evaluation
            </Typography>
          </Box>
        </Box>

        <Box sx={{
          bgcolor: 'white',
          borderRadius: '12px',
          p: 3,
          mb: 3
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
            Current Year Goals & Feedback
          </Typography>
          {employeeGoals.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No goals found for the current year.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {employeeGoals.map((goal) => (
                <Grid item xs={12} sm={6} lg={3} key={goal.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: 3, borderRadius: 2 }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                        {renderGoalTypeChip(goal.goal_type, goal.goal_detail === 'Predefined Personal Goals')}
                        <Chip
                          label={goal.status || 'Pending'}
                          color={getStatusColor(goal.status)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      {(goal.start_date && goal.end_date) && (
                        <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {formatDate(goal.start_date)} - {formatDate(goal.end_date)}
                        </Typography>
                      )}

                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          mb: 1,
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.main',
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={handleGoalDetailsPopupOpen}
                      >
                        {goal.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>Rating:</Typography>
                        <Rating value={goal.Ratings} readOnly precision={0.5} size="small" />
                        <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>({goal.Ratings?.toFixed(1) || '0.0'})</Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Feedback:
                      </Typography>
                      <Collapse in={expandedFeedbackId === goal.id} collapsedSize={60}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {goal.feedback || 'No feedback available'}
                        </Typography>
                      </Collapse>
                      {goal.feedback && goal.feedback.length > 100 && (
                        <Button
                          size="small"
                          onClick={() => handleToggleFeedback(goal.id)}
                          endIcon={expandedFeedbackId === goal.id ? <ExpandLess /> : <ExpandMore />}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          {expandedFeedbackId === goal.id ? 'Show Less' : 'Show More'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isSmallScreen ? 'small' : 'medium'}
              />
            </Box>
          )}
        </Box>

        <Box sx={{
          bgcolor: 'white',
          borderRadius: '12px',
          p: 3
        }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#333' }}>
            Add New Appraisal
          </Typography>
          <Grid container spacing={2} alignItems="center">

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel id="appraisal-period-label">Appraisal Period</InputLabel>
                <Select
                  labelId="appraisal-period-label"
                  id="appraisal-period-select"
                  value={appraisalPeriod}
                  label="Appraisal Period"
                  onChange={handlePeriodChange}
                >
                  <MenuItem value=""><em>Select a period</em></MenuItem>
                  {/* <MenuItem value="Q1">Quarter 1</MenuItem>
                  <MenuItem value="Q2">Quarter 2</MenuItem>
                  <MenuItem value="Q3">Quarter 3</MenuItem>
                  <MenuItem value="Q4">Quarter 4</MenuItem> */}

                  {/* Half-Yearly */}
                  {/* <MenuItem value="H1">Half-Yearly (Jan - Jun)</MenuItem>
                  <MenuItem value="H2">Half-Yearly (Jul - Dec)</MenuItem> */}

                  {/* Yearly */}
                  <MenuItem value='Quarterly'>Quarterly</MenuItem>
                  <MenuItem value='HalfYearly'>Half Yearly</MenuItem>
                  <MenuItem value="Yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Add Ratings</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              {appraisalPeriod && !showAppraisalForm && (
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutline />}
                  onClick={() => {
                    setShowAppraisalForm(true);
                    if (categories.length === 0) {
                      handleAddCategory();
                    }
                  }}
                  sx={{ mb: 3 }}
                >
                  Add Appraisal Details
                </Button>
              )}
            </Grid>
          </Grid>

          {showAppraisalForm && (
            <Box>
              {categories.map((category) => (
                <Box key={category.id} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: '8px', position: 'relative', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, position: 'absolute', top: 0, right: 0 }}>
                    {categories.length > 0 && (
                      <IconButton
                        aria-label="remove category"
                        onClick={() => handleRemoveCategory(category.id)}
                        color="error"
                        size="small"
                      >
                        <Cancel />
                      </IconButton>
                    )}
                  </Box>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel id={`category-name-label-${category.id}`}>Category Name</InputLabel>
                        <Select
                          labelId={`category-name-label-${category.id}`}
                          id={`category-name-select-${category.id}`}
                          value={category.name}
                          label="Category Name"
                          onChange={(e) => handleCategoryChange(category.id, 'name', e.target.value)}
                        >
                          <MenuItem value=""><em>Select a category</em></MenuItem>
                          {predefinedAppraisalCategories.map((catOption) => (
                            <MenuItem
                              key={catOption}
                              value={catOption}
                              disabled={
                                categories.some(c => c.name === catOption && c.id !== category.id)
                              }
                            >
                              {catOption}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}>Rating:</Typography>
                        <Rating
                          value={category.rating}
                          onChange={(event, newValue) => {
                            handleCategoryChange(category.id, 'rating', newValue);
                          }}
                          precision={0.5}
                          size="medium"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              {categories.length < predefinedAppraisalCategories.length && (
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={() => handleAddCategory()}
                  sx={{ mb: 3 }}
                  disabled={categories.length >= predefinedAppraisalCategories.length}
                >
                  Add Another Category
                </Button>
              )}
              <Grid container gap={2} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Add FeedBack</Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Strengths"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={feedback.strengths || ''}
                    onChange={(e) => setFeedback({ ...feedback, strengths: e.target.value })}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Areas for Improvement"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={feedback.areasForImprovement || ''}
                    onChange={(e) => setFeedback({ ...feedback, areasForImprovement: e.target.value })}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Goals for Next Period"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={feedback.goalsForNextPeriod || ''}
                    onChange={(e) => setFeedback({ ...feedback, goalsForNextPeriod: e.target.value })}
                    size="small"
                  />
                </Grid>
              </Grid>


              <Box sx={{ mt: 2 }}>
                {submitMessage && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: submitMessage.includes('Error') ? 'error.main' : 'success.main',
                        fontWeight: 'medium'
                      }}
                    >
                      {submitMessage}
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleSubmitAppraisal}
                  disabled={
                    !categories.every(cat => cat.name.trim() !== '' && cat.rating > 0) ||
                    isSubmitting ||
                    !feedback.strengths.trim() ||
                    !feedback.areasForImprovement.trim() ||
                    !feedback.goalsForNextPeriod.trim()
                  }
                  sx={{ position: 'relative' }}
                >
                  {isSubmitting ? 'Saving Appraisal...' : 'Save Appraisal'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

      </Box>

      <Modal
        open={isGoalDetailsPopupOpen}
        onClose={() => setIsGoalDetailsPopupOpen(false)}
      >
        <Box sx={{
          maxWidth: '80vw',
          maxHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          margin: 'auto',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          zIndex: "100"
        }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <GoalDetails />
          </Box>

          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
            <Button variant="outlined" color="primary" onClick={() => setIsGoalDetailsPopupOpen(false)}>
              CLOSE
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddPerformanceReviewAndRatings;